import { MediaItem } from '@/types/posts';
import { UploadState } from '@/hooks/useMediaUpload';
import { formatFileSize } from '@/utils/mediaValidation';
import { cn } from '@/utils/cn';
import { Tooltip } from '@/components/ui/Tooltip';

/**
 * MediaPreview Component
 *
 * Displays thumbnail preview of uploaded media with remove option
 */

interface MediaPreviewProps {
  media: MediaItem | UploadState;
  onRemove?: () => void;
  className?: string;
}

export const MediaPreview = ({ media, onRemove, className }: MediaPreviewProps) => {
  const isUploadState = 'status' in media;
  const isUploading = isUploadState && media.status === 'uploading';
  const hasError = isUploadState && media.status === 'error';
  const progress = isUploadState ? media.progress : 100;

  // Get preview URL (prefer thumbnail for PDFs)
  const previewUrl = isUploadState
    ? media.localPreview || media.media?.thumbnail || media.media?.url || ''
    : media.thumbnail || media.url || '';

  const filename = isUploadState ? media.filename : media.filename || 'Untitled';
  const fileSize = isUploadState ? media.size : media.size || 0;
  const mediaType = isUploadState ? media.type : media.type;
  // For deferred GDrive videos the url is empty; render thumbnail as <img> instead of <video>
  const isGdriveDeferred = !isUploadState && media.sourceType === 'GDRIVE' && !media.url;

  return (
    <div
      className={cn(
        'relative rounded-lg border overflow-hidden bg-gray-50',
        hasError ? 'border-red-300' : 'border-gray-200',
        className
      )}
    >
      {/* Thumbnail Preview */}
      <div className="aspect-square relative">
        {mediaType === 'pdf' && !previewUrl ? (
          // Fallback PDF icon when no thumbnail is available
          <div className="w-full h-full flex flex-col items-center justify-center bg-soft-coral">
            <svg
              className="w-16 h-16 text-bs-dark"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-xs font-medium text-red-600 mt-2">PDF</span>
          </div>
        ) : mediaType === 'pdf' && previewUrl ? (
          // PDF thumbnail preview
          <img
            src={previewUrl}
            alt={filename}
            className="w-full h-full object-cover"
          />
        ) : previewUrl ? (
          <>
            {mediaType === 'video' && !isGdriveDeferred ? (
              <video
                src={previewUrl}
                className="w-full h-full object-cover"
                muted
              />
            ) : (
              <img
                src={previewUrl}
                alt={filename}
                className="w-full h-full object-cover"
              />
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <svg
              className="w-12 h-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}

        {/* Uploading Overlay */}
        {isUploading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center">
            {/* Spinner */}
            <svg
              className="animate-spin h-8 w-8 text-white mb-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-white text-sm font-medium">
              {progress}%
            </span>
          </div>
        )}

        {/* Error Overlay */}
        {hasError && (
          <div className="absolute inset-0 bg-red-500 bg-opacity-20 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        )}

        {/* Remove Button */}
        {onRemove && !isUploading && (
          <button
            onClick={onRemove}
            className="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors shadow-md"
            aria-label="Remove media"
          >
            <span className="text-white text-sm font-bold leading-none">×</span>
          </button>
        )}

        {/* Media Type Badge (hidden while uploading/errored to avoid overlapping the progress overlay) */}
        {mediaType === 'video' && !isUploading && !hasError && (
          // eslint-disable-next-line i18next/no-literal-string
          <div className="absolute bottom-2 left-2 px-2 py-1 bg-black bg-opacity-60 rounded text-xs text-white">
            Video
          </div>
        )}
        {mediaType === 'pdf' && !isUploading && !hasError && (
          <div className="absolute bottom-2 left-2 px-2 py-1 bg-red-600 bg-opacity-90 rounded text-xs text-white font-medium">
            {/* eslint-disable-next-line i18next/no-literal-string */}
            PDF
          </div>
        )}
      </div>

      {/* File Info */}
      <div className="p-3 space-y-1">
        <Tooltip content={filename}>
          <p className="text-sm font-medium text-gray-900 truncate">
            {filename}
          </p>
        </Tooltip>
        <p className="text-xs text-gray-500">
          {formatFileSize(fileSize)}
        </p>

        {/* Error Message */}
        {hasError && 'error' in media && media.error && (
          <p className="text-xs text-red-600 mt-1">
            {media.error}
          </p>
        )}

        {/* Progress Bar */}
        {isUploading && (
          <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
            <div
              className="bg-primary-500 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
